<script>
import { mapGetters } from 'vuex';
import LoadIndicator from './LoadIndicator';
import Offering from './Offering';
import TagSelect from './TagSelect';

export default {
    name: 'ContentOfferBlock',
    components: {
        LoadIndicator,
        TagSelect,
        Offering
    },
    props: {
        data: Object,
    },
    data: () => {
        return {
            shownOfferings: 5,
            totalOffers: 0,
            selectedCategories: [],
            selectedLocations: [],
            showInitialMessage: false,
        }
    },
    mounted() {
        this.$store.dispatch('loadAllOfferings');
        this.$store.dispatch('loadAllOfferingCategories');
        this.$store.dispatch('loadAllOfferingLocations');

        this.shownOfferings = parseInt(this.data.number_of_items);

    },
    methods: {
        updateCategoryFilter(categories) {
            this.selectedCategories = categories;
            this.shownOfferings = parseInt(this.data.number_of_items);
        },
        updateLocationFilter(locations) {
            this.selectedLocations = locations;
            this.shownOfferings = parseInt(this.data.number_of_items);
        },
        isActiveCategory(category) {
            return (this.selectedCategories.indexOf(category) > -1);
        },
        onShowMore() {
            this.shownOfferings += parseInt(this.data.number_of_items);
        },
        compare(a, b) {
            a = a.order ? parseInt(a.order) : 0;
            b = b.order ? parseInt(b.order) : 0;
            if (a < b){
                return 1;
            }
            if (a > b){
                return -1;
            }
            return 0;
        },
        showCategoryFilter() {
            return this.data.filtering_options.indexOf('show_category_filter') > -1;
        },
        showLocationFilter() {
            return this.data.filtering_options.indexOf('show_location_filter') > -1 && this.locations.length > 1;
        }
    },
    computed: {
        ...mapGetters({
            isLoading: 'getOffersIsLoading',
            allOffers: 'getAllOffers',
            categories: 'getAllOfferCategories',
            allLocations: 'getAllOfferLocations',
        }),
        filteredOffers() {
            let data = [];
            this.showInitialMessage = false;

            // pre filter with selected categies from backend
            const offers = this.allOffers.filter((item) => {
                let inSelection = false;
                item.offer_locations.forEach(tagId => {
                    if (this.data.locations.find(tag => tag === tagId)) {
                        inSelection = true;
                    }
                })
                return inSelection;
            })

            // filter data if any filter is set
            if (this.selectedCategories.length > 0 || this.selectedLocations.length > 0) {
                data = offers.filter(item => {
                    // loop categories
                    let isInCategory = false;
                    if (this.selectedCategories.length > 0) {
                        item.offer_categories.forEach(tagId => {
                            if (this.selectedCategories.find(tag => tag.id === tagId))
                                isInCategory = true;
                        })
                    } else {
                        isInCategory = true;
                    }

                    // loop locations
                    let isInLocation = false;
                    if (this.selectedLocations.length > 0) {
                        item.offer_locations.forEach(tagId => {
                            if (this.selectedLocations.find(tag => tag.id === tagId))
                                isInLocation = true;
                        })
                    } else {
                        isInLocation = true;
                    }

                    return isInCategory && isInLocation;

                });
                data.sort(this.compare);
            }

            // select featured if no filters are applied and more then one location is selectable
            else if (!this.showLocationFilter()) {
                let featured = offers.filter(item => item.featured);
                let notFeatured = offers.filter(item => !item.featured);
                featured.sort(this.compare);
                notFeatured.sort(this.compare);
                data = [...featured, ...notFeatured];
            }

            else {
                this.showInitialMessage = true;
            }

            this.totalOffers = data.length;
            return data.slice(0, this.shownOfferings);

        },
        locations() {
            return this.allLocations.filter(item => this.data.locations.indexOf(item.id) > -1)
        }
    }
};
</script>

<template>
    <div class="offerings-block">
        <h2 class="offerings-block__title" v-if="data.title" v-html="data.title"></h2>

        <div class="offerings-block__filters" v-if="!isLoading">
            <tag-select 
                v-if="showLocationFilter()"
                v-bind:tags="locations" 
                v-bind:placeholder="$t('offeringsBlock.chooseLocation')" 
                v-on:tagsChanged="updateLocationFilter($event)" />
            <tag-select 
                v-if="showCategoryFilter()"
                v-bind:tags="categories" 
                v-bind:placeholder="$t('offeringsBlock.chooseCategory')" 
                v-on:tagsChanged="updateCategoryFilter($event)" />
        </div>

        <div class="offerings-block__list">
            <div class="offerings-block__list__body">
                <offering 
                    v-for="(offering, i) in filteredOffers" 
                    v-bind:key="i"
                    v-bind:title="offering.title.rendered"
                    v-bind:description="offering.description"
                    v-bind:organisation="offering.organisation"
                    v-bind:link="offering.link" 
                    v-bind:featured="offering.featured" />

                <div class="offerings-block__list__no-items" 
                    v-if="filteredOffers.length === 0 && !isLoading && !showInitialMessage">
                        {{ $t('offeringsBlock.noItems') }}
                </div>

                <div class="offerings-block__list__initial-message" 
                    v-if="showInitialMessage && !isLoading">
                        {{ $t('offeringsBlock.initialMessage') }}
                </div>

                <load-indicator v-if="isLoading" />

            </div>

            <div class="offerings-block__list__footer">
                <a  class="offerings-block__list__load-more" 
                    v-if="totalOffers > shownOfferings && !isLoading" 
                    v-on:click="onShowMore">{{ $t('offeringsBlock.loadMore') }}
                </a>
            </div>
            
        </div>

    </div>
</template>

<style lang="scss" scoped>
.offerings-block {
    margin: 9.6rem 0 11.2rem;

    @include breakpoint('sm-down') {
        margin: 7.2rem 0 8.8rem;
    }
}
.offerings-block__title {
    margin: 0 0 4.8rem;
    font-weight: 700;
    font-size: 3.2rem;
}
.offerings-block__filters {
    display: flex;
    width: calc(100% + 1.6rem);
    margin: 0 -0.8rem 1.92rem;

    @include breakpoint('sm-down') {
        display: block;
        margin: 0;
        width: 100%;
    }
}
.tag-select {
    margin: 0 0.8rem;

    @include breakpoint('sm-down') {
        margin: 0.8rem 0 0.8rem;
    }
}
.offerings-block__list__body {
    border-top: 0.1rem solid rgba($color-primary, .15);
    border-bottom: 0.1rem solid rgba($color-primary, .15);
}
.offerings-block__list__no-items,
.offerings-block__list__initial-message {
    padding: 3.2rem 0;
    text-align: center;
}
.offering:last-child {
    border-bottom: none;
}
.load-indicator {
    padding: 3.2rem 0;
    text-align: center;
}
.offerings-block__list__footer {
    padding: 0.8rem 0;
    text-align: center;
}
.offerings-block__list__load-more {
    font-size: 1.4rem;
    color: $color-secondary;
    letter-spacing: 0.02857142857em;
    line-height: 1;
    text-transform: uppercase;
    text-decoration: none;
    cursor: pointer;
}
</style>
